import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import { 
  Crown, 
  Check, 
  DollarSign, 
  Percent, 
  Shield, 
  Zap, 
  TrendingUp, 
  Users, 
  Calculator,
  ArrowRight,
  Star,
  Package,
  CreditCard,
  Gift,
  Tag
} from 'lucide-react';

const comparisonData = [
  { platform: 'eBay', fee: '13.25%', onSale: '$100', youKeep: '$86.75' },
  { platform: 'COMC', fee: '5-20%', onSale: '$100', youKeep: '$80-95' },
  { platform: 'MySlabs', fee: '8%', onSale: '$100', youKeep: '$92' },
  { platform: 'Slab Society', fee: '0%', onSale: '$100', youKeep: '$100', highlight: true },
];

const benefits = [
  {
    icon: Percent,
    title: '0% Seller Commission',
    description: 'Keep every dollar you earn. No hidden fees, no percentage cuts. Your $100 sale = $100 in your pocket.',
    highlight: true
  },
  {
    icon: Zap,
    title: 'AI-Powered Listings',
    description: 'Upload a photo and our AI instantly identifies player, set, year, and grade. List cards in seconds, not minutes.',
  },
  {
    icon: Package,
    title: 'Unlimited Listings',
    description: 'List as many cards as you want. No caps, no tiers, no extra charges. One flat monthly fee covers everything.',
  },
  {
    icon: Shield,
    title: 'Verified Seller Badge',
    description: 'Stand out with a verified badge that builds buyer trust and increases your conversion rates.',
  },
  {
    icon: TrendingUp,
    title: 'Premium Visibility',
    description: 'Your listings get priority placement in search results and featured sections.',
  },
  {
    icon: Users,
    title: 'Collector Community',
    description: 'Join a growing community of serious collectors and investors who value quality over quantity.',
  },
];

const testimonials = [
  {
    quote: "I sold $2,400 worth of cards last month. On eBay, I would have lost $318 to fees. With Slab Society, I kept it all.",
    author: "Mike T.",
    title: "Basketball Card Investor",
    savings: "$318"
  },
  {
    quote: "The AI scanner is a game-changer. What used to take me 10 minutes per listing now takes 30 seconds.",
    author: "Sarah K.",
    title: "Vintage Card Collector",
    savings: "$156"
  },
  {
    quote: "Finally, a platform that respects sellers. The $20/month pays for itself after selling just one card.",
    author: "James R.",
    title: "Sports Memorabilia Dealer",
    savings: "$892"
  },
];

const faqs = [
  {
    q: "How can you offer 0% seller fees?",
    a: "We charge buyers a small 3.5% transaction fee instead of taking from sellers. This model lets us operate sustainably while you keep 100% of your sale price."
  },
  {
    q: "What if I only sell a few cards per month?",
    a: "Even if you sell just one $150+ card per month, you're already saving money compared to eBay's 13.25% fees. The more you sell, the more you save."
  },
  {
    q: "Can I cancel anytime?",
    a: "Absolutely. No contracts, no commitments. Cancel with one click and your membership ends at the end of your billing period."
  },
  {
    q: "What happens to my listings if I cancel?",
    a: "Your active listings remain live until the end of your billing period. After that, they'll be archived but not deleted—you can reactivate them anytime."
  },
];

export const MembershipBenefitsPage = () => {
  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="noise-overlay" />
      <Navbar />
      
      {/* Promo Banner */}
      <div className="bg-gradient-to-r from-amber-500/20 via-amber-500/10 to-amber-500/20 border-b border-amber-500/30 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-center gap-3 text-center flex-wrap">
            <Gift className="w-6 h-6 text-amber-400 shrink-0" />
            <div>
              <p className="text-base sm:text-lg text-white font-semibold">
                <span className="text-amber-400">Limited Time Offer:</span> Get your first month FREE!
              </p>
              <p className="text-sm text-zinc-400">
                Use code <span className="font-mono font-bold bg-amber-500/30 px-2 py-0.5 rounded text-amber-300">SLAB100</span> at checkout
              </p>
            </div>
            <Link to="/register">
              <Button className="bg-amber-500 hover:bg-amber-400 text-black font-semibold px-4">
                Claim Offer
              </Button>
            </Link>
          </div>
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="relative pt-16 pb-20 px-4 sm:px-6 lg:px-8 spotlight overflow-hidden">
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter uppercase text-white leading-[0.9]">
            Keep <span className="text-emerald-400">100%</span> of<br />
            Every Sale
          </h1>
          
          <p className="mt-8 text-xl sm:text-2xl text-zinc-400 max-w-3xl mx-auto leading-relaxed">
            While other platforms take <span className="text-red-400 font-semibold">13-20% of your earnings</span>, 
            Slab Society members keep every dollar. Join for just $20/month.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
            <Link to="/register">
              <Button className="btn-primary text-lg px-10 h-14" data-testid="hero-join-btn">
                Start Selling Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Floating Stats */}
        <div className="max-w-4xl mx-auto mt-20 grid grid-cols-3 gap-4">
          <div className="stat-card p-6 text-center">
            <p className="font-mono text-4xl font-bold text-emerald-400">$0</p>
            <p className="text-zinc-500 text-sm mt-1">Seller Commission</p>
          </div>
          <div className="stat-card p-6 text-center">
            <p className="font-mono text-4xl font-bold text-white">$20</p>
            <p className="text-zinc-500 text-sm mt-1">Per Month</p>
          </div>
          <div className="stat-card p-6 text-center">
            <p className="font-mono text-4xl font-bold text-amber-400">∞</p>
            <p className="text-zinc-500 text-sm mt-1">Unlimited Listings</p>
          </div>
        </div>
      </section>

      {/* Fee Comparison Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#0C0C0E]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="badge badge-blue mb-4">The Math Doesn't Lie</span>
            <h2 className="font-heading font-bold text-3xl sm:text-5xl tracking-tight uppercase text-white">
              Stop Losing Money to Fees
            </h2>
            <p className="text-zinc-400 mt-4 text-lg">See how much you keep on a $100 sale</p>
          </div>

          <div className="stat-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-zinc-400 font-medium">Platform</th>
                  <th className="text-center p-4 text-zinc-400 font-medium">Fee</th>
                  <th className="text-center p-4 text-zinc-400 font-medium hidden sm:table-cell">Sale Price</th>
                  <th className="text-right p-4 text-zinc-400 font-medium">You Keep</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, i) => (
                  <tr 
                    key={row.platform} 
                    className={`border-b border-white/5 ${row.highlight ? 'bg-emerald-500/10' : ''}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {row.highlight && <Crown className="w-5 h-5 text-amber-500" />}
                        <span className={`font-medium ${row.highlight ? 'text-white' : 'text-zinc-300'}`}>
                          {row.platform}
                        </span>
                      </div>
                    </td>
                    <td className={`text-center p-4 font-mono ${row.highlight ? 'text-emerald-400 font-bold' : 'text-red-400'}`}>
                      {row.fee}
                    </td>
                    <td className="text-center p-4 text-zinc-400 hidden sm:table-cell">{row.onSale}</td>
                    <td className={`text-right p-4 font-mono font-bold ${row.highlight ? 'text-emerald-400 text-xl' : 'text-zinc-300'}`}>
                      {row.youKeep}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/20">
            <div className="flex items-center gap-4">
              <Calculator className="w-10 h-10 text-emerald-400 flex-shrink-0" />
              <div>
                <p className="text-white font-semibold">Quick Math: Sell $1,500/month?</p>
                <p className="text-zinc-400">
                  On eBay: <span className="text-red-400">-$198.75</span> in fees. 
                  On Slab Society: <span className="text-emerald-400">-$20</span> flat. 
                  You save <span className="text-emerald-400 font-bold">$178.75/month</span>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="badge badge-amber mb-4">Membership Benefits</span>
            <h2 className="font-heading font-bold text-3xl sm:text-5xl tracking-tight uppercase text-white">
              Everything You Need to Sell
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, i) => (
              <div 
                key={benefit.title}
                className={`stat-card p-6 hover:border-white/10 transition-all group ${
                  benefit.highlight ? 'md:col-span-2 lg:col-span-1 border-emerald-500/30 bg-emerald-500/5' : ''
                }`}
              >
                <benefit.icon className={`w-10 h-10 mb-4 ${benefit.highlight ? 'text-emerald-400' : 'text-blue-400'}`} />
                <h3 className="font-heading font-bold text-xl text-white mb-2 group-hover:text-blue-400 transition-colors">
                  {benefit.title}
                </h3>
                <p className="text-zinc-400 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#0C0C0E]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="badge badge-emerald mb-4">Success Stories</span>
            <h2 className="font-heading font-bold text-3xl sm:text-5xl tracking-tight uppercase text-white">
              Sellers Love Us
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="stat-card p-6 flex flex-col">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-500 text-amber-500" />
                  ))}
                </div>
                <blockquote className="text-zinc-300 leading-relaxed flex-1">
                  "{t.quote}"
                </blockquote>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="font-medium text-white">{t.author}</p>
                  <p className="text-sm text-zinc-500">{t.title}</p>
                  <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 text-sm">
                    <DollarSign className="w-3 h-3" />
                    {t.savings} saved last month
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="badge badge-amber mb-4">Seller Membership</span>
            <h2 className="font-heading font-bold text-3xl sm:text-5xl tracking-tight uppercase text-white mb-4">
              Choose Your Plan
            </h2>
            <p className="text-zinc-400 text-lg">Both plans include all features. Save $40 with annual billing.</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Monthly Plan */}
            <div className="stat-card p-8 text-center relative overflow-hidden border-white/10 hover:border-blue-500/50 transition-colors">
              <div className="relative z-10">
                <h3 className="font-heading font-bold text-2xl text-white mb-2">Monthly</h3>
                <p className="text-zinc-500 text-sm mb-6">Flexible, cancel anytime</p>
                
                <div className="mb-8">
                  <p className="font-mono text-5xl font-bold text-white">
                    $20<span className="text-xl text-zinc-500">/mo</span>
                  </p>
                  <p className="text-zinc-500 text-sm mt-2">Billed monthly</p>
                </div>

                <div className="space-y-3 text-left mb-8">
                  {['0% seller commission', 'Unlimited listings', 'AI card scanner', 'Verified seller badge'].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Check className="w-3 h-3 text-emerald-400" />
                      </div>
                      <span className="text-zinc-300 text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <Link to="/register">
                  <Button className="w-full h-12 bg-zinc-700 hover:bg-zinc-600 text-white" data-testid="monthly-plan-btn">
                    Select Monthly
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Annual Plan - Recommended */}
            <div className="stat-card p-8 text-center relative overflow-hidden border-emerald-500/50 bg-gradient-to-br from-emerald-500/5 to-blue-500/5">
              {/* Save Badge */}
              <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold">
                SAVE $40
              </div>
              
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-blue-500/10" />
              
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/30 mb-4">
                  <Crown className="w-4 h-4 text-amber-500" />
                  <span className="text-amber-400 text-xs font-semibold">BEST VALUE</span>
                </div>
                
                <h3 className="font-heading font-bold text-2xl text-white mb-2">Annual</h3>
                <p className="text-zinc-500 text-sm mb-6">Best value, save 17%</p>
                
                <div className="mb-8">
                  <p className="font-mono text-5xl font-bold text-emerald-400">
                    $200<span className="text-xl text-zinc-500">/yr</span>
                  </p>
                  <p className="text-zinc-400 text-sm mt-2">
                    <span className="line-through text-zinc-600">$240</span> Billed annually
                  </p>
                </div>

                <div className="space-y-3 text-left mb-8">
                  {['0% seller commission', 'Unlimited listings', 'AI card scanner', 'Verified seller badge', 'Priority support', '2 months FREE'].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Check className="w-3 h-3 text-emerald-400" />
                      </div>
                      <span className={`text-sm ${i === 5 ? 'text-emerald-400 font-semibold' : 'text-zinc-300'}`}>{item}</span>
                    </div>
                  ))}
                </div>

                <Link to="/register?plan=yearly">
                  <Button className="w-full h-12 btn-primary" data-testid="annual-plan-btn">
                    Select Annual & Save
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
          
          <p className="text-zinc-500 text-sm mt-8 text-center">
            30-day money-back guarantee on all plans
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#0C0C0E]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <span className="badge badge-blue mb-4">FAQ</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight uppercase text-white">
              Common Questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="stat-card p-6">
                <h3 className="font-medium text-white mb-2">{faq.q}</h3>
                <p className="text-zinc-400">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-5xl tracking-tight uppercase text-white mb-6">
            Ready to Keep More Money?
          </h2>
          <p className="text-xl text-zinc-400 mb-10">
            Join thousands of collectors who switched to Slab Society.
          </p>
          <Link to="/register">
            <Button className="btn-primary text-lg px-12 h-14" data-testid="final-join-btn">
              Create Free Account
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <p className="text-zinc-500 text-sm mt-4">
            Free to browse & buy. $20/month to sell.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img 
              src={process.env.REACT_APP_LOGO_URL} 
              alt="Slab Society"
              className="w-8 h-8 rounded-lg object-contain"
            />
            <span className="text-zinc-500">© 2024 Slab Society. Founded by Ron Manthei</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-zinc-500">
            <Link to="/marketplace" className="hover:text-white transition-colors">Marketplace</Link>
            <Link to="/register" className="hover:text-white transition-colors">Sign Up</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MembershipBenefitsPage;
